import { ImagePlus, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import type { ContentRegion } from "./content-regions.ts";
import type { Screenshot } from "./screenshot.ts";

/** What the form sends with the report: the previewed PNG and whether a region was shown. */
export interface AttachedScreenshot {
  blob: Blob;
  unmasked: boolean;
}

export interface ScreenshotSource {
  /** The content regions on screen (read when the user adds a screenshot). */
  regions: () => ContentRegion[];
  /** The regions an unmask would draw, given what is unmasked (none inside a masked region). */
  reachable: (unmasked: ReadonlySet<ContentRegion>) => ContentRegion[];
  /** Captures the page with every region masked but `unmasked`. */
  capture: (unmasked: ReadonlySet<ContentRegion>) => Promise<Screenshot>;
}

/**
 * The screenshot row of "What will be sent" (openspec change `in-app-feedback`, design D4):
 * nothing until the user adds one; then the PNG exactly as it will be uploaded, masked, and a
 * checkbox per content region on the page to show it for this send only. Every change takes a
 * new capture, so the preview is always the image sent. State lives with the dialog: the next
 * send starts masked and without a screenshot.
 */
export function ScreenshotField(props: {
  source: ScreenshotSource;
  disabled?: boolean;
  onChange: (screenshot: AttachedScreenshot | null) => void;
  /** A capture is running: the form must not send until it settles. */
  onBusy?: (busy: boolean) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [regions, setRegions] = useState<ContentRegion[] | null>(null);
  const [unmasked, setUnmasked] = useState<ReadonlySet<ContentRegion>>(new Set());
  const [reachable, setReachable] = useState<ReadonlySet<ContentRegion>>(new Set());
  const [shot, setShot] = useState<{ url: string; screenshot: Screenshot } | null>(null);
  const [taking, setTaking] = useState(false);
  const [failed, setFailed] = useState(false);
  // The latest capture wins: a slower earlier one never replaces it.
  const attempt = useRef(0);
  const { onChange, onBusy, source } = props;
  useEffect(() => onBusy?.(taking), [taking, onBusy]);

  useEffect(() => () => (shot ? URL.revokeObjectURL(shot.url) : undefined), [shot]);

  const take = async (shown: ReadonlySet<ContentRegion>) => {
    const mine = ++attempt.current;
    setTaking(true);
    setFailed(false);
    // The previous image no longer matches the choice: nothing is attached until the new one is.
    onChange(null);
    try {
      const screenshot = await source.capture(shown);
      if (mine !== attempt.current) {
        return;
      }
      setShot({ url: URL.createObjectURL(screenshot.blob), screenshot });
      onChange({ blob: screenshot.blob, unmasked: shown.size > 0 });
    } catch {
      if (mine !== attempt.current) {
        return;
      }
      setShot(null);
      setRegions(null);
      setUnmasked(new Set());
      setFailed(true);
      onChange(null);
    } finally {
      if (mine === attempt.current) {
        setTaking(false);
      }
    }
  };

  /** Keeps only what is drawn: a region inside a masked one is unticked and disabled. */
  const settle = (wanted: ReadonlySet<ContentRegion>): Set<ContentRegion> => {
    let current = new Set(wanted);
    for (;;) {
      const open = new Set(source.reachable(current));
      const next = new Set([...current].filter((r) => open.has(r)));
      if (next.size === current.size) {
        setReachable(open);
        return next;
      }
      current = next;
    }
  };

  const add = () => {
    setRegions(source.regions());
    setUnmasked(settle(new Set()));
    void take(new Set());
  };
  const remove = () => {
    attempt.current++;
    setShot(null);
    setRegions(null);
    setUnmasked(new Set());
    setTaking(false);
    onChange(null);
  };
  const toggle = (region: ContentRegion, show: boolean) => {
    const next = new Set(unmasked);
    if (show) {
      next.add(region);
    } else {
      next.delete(region);
    }
    const drawn = settle(next);
    setUnmasked(drawn);
    void take(drawn);
  };

  const shownNames = [...unmasked].map((r) => t(`feedbackScreenshot.regions.${r}`)).join(", ");

  return (
    <li className="flex min-w-0 flex-col gap-2">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span id={`${id}-label`} className="text-sm text-fg">
            {t("feedbackScreenshot.label")}
          </span>
          <span className="text-xs text-fg-muted">{t("feedbackScreenshot.lead")}</span>
        </div>
        {regions === null ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            disabled={props.disabled || taking}
            onClick={add}
          >
            <ImagePlus aria-hidden="true" strokeWidth={1.75} />
            {taking ? t("feedbackScreenshot.taking") : t("feedbackScreenshot.add")}
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0"
            disabled={props.disabled}
            onClick={remove}
            aria-describedby={`${id}-label`}
          >
            <X aria-hidden="true" strokeWidth={1.75} />
            {t("feedbackScreenshot.remove")}
          </Button>
        )}
      </div>

      {failed ? <Notice tone="warning">{t("feedbackScreenshot.failed")}</Notice> : null}

      {regions !== null ? (
        <div className="flex min-w-0 flex-col gap-3">
          <figure className="flex min-w-0 flex-col gap-1.5" aria-busy={taking || undefined}>
            <div className="relative flex min-h-24 items-center justify-center overflow-hidden rounded-md border border-line bg-surface-sunken">
              {shot ? (
                <img
                  src={shot.url}
                  alt={t("feedbackScreenshot.alt")}
                  className={`max-h-56 w-full object-contain transition-opacity duration-(--duration-fast) ${taking ? "opacity-50" : ""}`}
                />
              ) : null}
              {taking && !shot ? (
                <span className="text-xs text-fg-muted">{t("feedbackScreenshot.taking")}</span>
              ) : null}
            </div>
            <figcaption className="text-xs text-fg-secondary" aria-live="polite">
              {unmasked.size === 0
                ? t("feedbackScreenshot.allHidden")
                : t("feedbackScreenshot.shown", { regions: shownNames })}
            </figcaption>
          </figure>
          {regions.length > 0 ? (
            <fieldset className="flex min-w-0 flex-col gap-2">
              <legend className="mb-1 text-xs font-medium text-fg-secondary">
                {t("feedbackScreenshot.show")}
              </legend>
              <div className="grid gap-x-4 gap-y-2 sm:grid-cols-2">
                {regions.map((region) => (
                  <div key={region} className="flex min-w-0 items-center gap-2">
                    <Checkbox
                      id={`${id}-${region}`}
                      checked={unmasked.has(region)}
                      disabled={props.disabled || taking || !reachable.has(region)}
                      onCheckedChange={(checked) => toggle(region, checked === true)}
                    />
                    <Label htmlFor={`${id}-${region}`} className="text-sm font-normal">
                      {t(`feedbackScreenshot.regions.${region}`)}
                    </Label>
                  </div>
                ))}
              </div>
            </fieldset>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
